<template>
    <div ref="vatPickerWrapper" v-on-click-outside="hideVatPicker">
        <div class="@container w-full h-full cursor-pointer flex items-center pl-0 relative" @click="show">
            <label v-if="label" for="vatPickerInput" class="label">{{ $t(label) }}<span class="label-background"/></label>
            <div
                id="vatPickerInputContainer"
                ref="vatPickerInputContainer"
                class="w-full relative"
            >
                <input
                    id="vatPickerInput"
                    ref="vatPickerInput"
                    :value="vatOption?.code"
                    class="input input-sm focus:outline-none w-full border border-base-300 rounded-none"
                    :data-cy="dataCy"
                    :disabled="!editable"
                    :title="!editable ? 'Ikke tillat ved valgt konto' : ''"
                    autocomplete="off"
                    @focus="show"
                    @focusout="hideVatPicker"
                    @click="show"
                    @keydown.up="onKeyUp"
                    @keydown.down="onKeyDown"
                    @keydown.enter="selectHighlighted"
                    @keydown.delete="deSelectOption"
                    @keydown.esc.stop="hideVatPicker"
                    @keydown.tab.exact="selectOption"
                    @keydown.shift.tab="selectOption"
                >
                <div
                    v-if="!selectorIsOpen"
                    class="input input-sm border border-base-300 rounded-none px-0 @[90px]:px-1 flex justify-between truncate overflow-hidden text-ellipsis gap-3 absolute top-0 pointer-events-none w-full"
                    :class="{ 'text-gray-400': !editable }"
                    :title="`${vatOption?.code} ${selectedOptionDisplayPercentage}: ${formatAmount}` "
                >
                    <template v-if="vatOption">
                        <div class="px-1">
                            <span class="hidden @[90px]:inline-block">{{ vatOption.code }}&nbsp;-&nbsp;</span>
                            <span>{{ selectedOptionDisplayPercentage }}</span>
                        </div>
                        <div class="hidden @[120px]:block">{{ formatAmount }}</div>
                    </template>
                </div>
            </div>
        </div>
        <teleport to="body" :disabled="disableTeleport">
            <div
                v-if="selectorIsOpen"
                ref="vatPickerContainer"
                class="vat-picker-container z-10"
                :style="{top: scrollerPos.top + 'px', left: scrollerPos.left + 'px'}"
            >
                <div
                    ref="scroller"
                    class="new-vat-picker-scroller"
                >
                    <div
                        v-for="option in sortedVatOptions"
                        :key="option.id"
                        class="new-vat-picker-option"
                        :class="[{highlighted: option.id === highlightedOption?.id}]"
                        :title="$t(`vat-codes.${option.code}`)"
                        @mousedown="selectHighlighted"
                        @mousemove="() => onMouseEnter(option)"
                    >
                        <span class="col-span-2">{{ option.code }} {{ '(' + formatPercentage(option.displayedRate) + ')' }}</span>
                        <div class="col-span-6">
                            <span v-if="option.specificationKey" class="mr-1">{{ $t('vat-codes.' + option.specificationKey) + ':' }}</span>
                            <span>{{ $t('vat-codes.' + option.code) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script setup lang="ts">
import {vOnClickOutside} from '@vueuse/components'
import {calculateVatAmount, useVatService} from '@/vat/vat.service'
import {formatCurrency, formatPercentage} from '@/services/formatter.service'
import {computed, nextTick, ref} from 'vue'
import {WINDOW_SCROLL_START} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'
import {findIndex} from 'lodash'
import {VatOption} from '@/vat/types/VatOptions'

const {onEvent} = useEmitter()
const {vatOptions, getLabelForVatPicker} = useVatService()

const vatOption = defineModel<VatOption | undefined>('vatOption', {required: true})
const props = defineProps({
    amount: {type: Number, required: true},
    editable: {type: Boolean, default: true},
    dataCy: {type: String, default: 'vat-picker-input'},
    disableTeleport: {type: Boolean, default: false}
})

const selectorIsOpen = ref(false)

const vatPickerContainer = ref<HTMLDivElement | null>(null)
const vatPickerWrapper = ref<HTMLDivElement | null>(null)
const vatPickerInputContainer = ref<HTMLDivElement | null>(null)
const vatPickerInput = ref<HTMLInputElement | null>(null)
const highlightedOption = ref<VatOption | undefined>(undefined)

const scrollerPos = ref({top: 0, left: 0})

const sortedVatOptions = computed<VatOption[]>(() => {
    return [...vatOptions.value].sort((a, b) => {
        return a.code.toString().localeCompare(b.code.toString())
    })
})

const label = computed(() => {
    if (!vatOption.value) return ''

    return getLabelForVatPicker(vatOption.value)
})

const vatAmount = computed(() => {
    if (!vatOption.value || !props.amount) return

    return calculateVatAmount(vatOption.value, props.amount)
})

const setScrollPos = () => {
    const inputContainerRect = vatPickerInputContainer.value?.getBoundingClientRect() || {top: 0, left: 0, height: 32}

    const wrapperRectBottom = vatPickerWrapper.value?.getBoundingClientRect().bottom
    const breadCrumbsBottom = document.getElementById('breadcrumbs')?.getBoundingClientRect().bottom

    if (wrapperRectBottom && breadCrumbsBottom) {
        const isWrapperRectVisible = wrapperRectBottom > breadCrumbsBottom

        scrollerPos.value.top = isWrapperRectVisible ? inputContainerRect.top + inputContainerRect.height + 12 : breadCrumbsBottom + 12
        scrollerPos.value.left = inputContainerRect.left
    }
}

const show = () => {
    if (props.editable) {
        selectorIsOpen.value = true
        highlightedOption.value = vatOption.value || sortedVatOptions.value[0]
    }
    setScrollPos()
    nextTick(() => vatPickerInput.value?.focus())
}

const hideVatPicker = () => selectorIsOpen.value = false

const selectHighlighted = () => {
    vatOption.value = highlightedOption.value
    hideVatPicker()
}

const deSelectOption = () => {
    vatOption.value = undefined
    hideVatPicker()
}

const selectedOptionDisplayPercentage = computed(() => {
    if (!vatOption.value) return '0%'
    return `${formatPercentage(vatOption.value.displayedRate)}`
})

const formatAmount = computed(() => {
    if (!vatOption.value) return ''
    return formatCurrency(vatAmount.value, 2)
})

const selectOption = () => {
    if (!highlightedOption.value) {
        vatOption.value = undefined
        hideVatPicker()
    } else {
        selectHighlighted()
    }
}

const onKeyDown = () => {
    let existingIndex = -1
    if (highlightedOption.value) {
        existingIndex = findIndex(sortedVatOptions.value, {id: highlightedOption.value.id})
    }
    const nextOption = sortedVatOptions.value[existingIndex + 1]
    if (nextOption) {
        highlightedOption.value = nextOption
        nextTick(() => {
            const container = vatPickerContainer.value
            const optionEl = container?.querySelector('.new-vat-picker-option.highlighted')
            optionEl?.scrollIntoView({block: 'nearest'})
        })
    }
}

const onKeyUp = () => {
    if (!highlightedOption.value) {
        return
    }
    const existingIndex = findIndex(sortedVatOptions.value, {id: highlightedOption.value.id})
    const prevOption = sortedVatOptions.value[existingIndex - 1]
    if (prevOption) {
        highlightedOption.value = prevOption
        nextTick(() => {
            const container = vatPickerContainer.value
            const optionEl = container?.querySelector('.new-vat-picker-option.highlighted')
            optionEl?.scrollIntoView({block: 'nearest'})
        })
    }
}

const onMouseEnter = (option: VatOption) => {
    const existingIndex = findIndex(vatOptions.value, {id: option.id})
    const hoveredOption = vatOptions.value[existingIndex]
    if (hoveredOption) {
        highlightedOption.value = hoveredOption
    }
}

onEvent(WINDOW_SCROLL_START, () => {
    if (selectorIsOpen.value) {
        setScrollPos()
    }
})

</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.vat-picker-container {
    @apply absolute overflow-auto top-0 left-0 bg-base-100 border border-base-300 rounded shadow-xl h-[500px] w-86 text-sm;
}

.new-vat-picker-scroller {
    @apply max-h-full top-0 max-w-full shadow-md bg-base-100;

    .new-vat-picker-option {
        @apply cursor-pointer grid grid-cols-8 p-1;

        &.highlighted {
            @apply bg-base-200;
        }
    }

    &.focused {
        @apply outline-primary/50 opacity-100;
        li.multiselect__element {
            @apply bg-white;
        }
    }
}

</style>